// Head-data för definitionssidor: DefinedTerm + FAQPage + breadcrumbs.
// En enda plats så alla definitionssidor märks upp likadant.

import { aeoGeo, breadcrumbs, faqPage } from "@/lib/seo/schema";
import { abs, ORG_ID } from "@/lib/seo/constants";
import { PRICE_DATA_BASIS } from "@/content/pricing-facts";
import { COMPANY_FACTS } from "@/content/company-facts";
import { EDITORIAL_PEOPLE, authorId, authorPath } from "@/content/editorial";
import type { DefinitionEntry } from "@/content/definitions";

export function definitionHead(entry: DefinitionEntry) {
  const path = `/ordlista/${entry.slug}`;
  const url = abs(path);
  const title = `${entry.term}: betydelse och exempel | Fenny`;

  const definedTerm = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": `${url}#term`,
    name: entry.term,
    alternateName: entry.synonyms,
    description: entry.definition,
    url,
    inLanguage: "sv-SE",
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "Fenny — filmordlista",
      url: abs("/ordlista"),
    },
    subjectOf: {
      "@type": "WebPage",
      url,
      name: title,
      datePublished: PRICE_DATA_BASIS.reviewed,
      dateModified: PRICE_DATA_BASIS.reviewed,
      author: {
        "@type": "Person",
        "@id": authorId("mattias"),
        name: EDITORIAL_PEOPLE.mattias.name,
        jobTitle: EDITORIAL_PEOPLE.mattias.role,
        url: `${COMPANY_FACTS.site}${authorPath("mattias")}`,
        ...(EDITORIAL_PEOPLE.mattias.linkedin
          ? { sameAs: [EDITORIAL_PEOPLE.mattias.linkedin] }
          : {}),
        worksFor: { "@id": ORG_ID },
      },
      publisher: { "@id": ORG_ID },
    },
  };

  return {
    meta: [
      { title },
      { name: "description", content: entry.metaDescription },
      { property: "og:title", content: title },
      { property: "og:description", content: entry.metaDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          aeoGeo({
            path,
            name: title,
            description: entry.metaDescription,
            question: `Vad är ${entry.term.toLowerCase()}?`,
            answer: entry.definition,
            dateModified: PRICE_DATA_BASIS.reviewed,
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(definedTerm) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(entry.faq)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Ordlista", path: "/ordlista" },
            { name: entry.term, path },
          ]),
        ),
      },
    ],
  };
}
